import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { MainLayout } from '@/components/layout/MainLayout';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useAuthStore } from '@/stores/authStore';
import { useTenantStore } from '@/stores/tenantStore';
import { hasNavAccess } from '@/utils/permissions';
import api from '@/api/axios';
import type { AuthUser } from '@/api/types/auth.types';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const TrackingPage = lazy(() => import('@/features/tracking/pages/TrackingPage'));
const CustomerIntakePage = lazy(() => import('@/features/intake/pages/CustomerIntakePage'));
const KioskPage = lazy(() => import('@/features/intake/pages/KioskPage'));
const SelfIntakeSendPage = lazy(() => import('@/features/intake/pages/SelfIntakeSendPage'));
// POC only - not a real feature.
const OcrPocPage = lazy(() => import('@/features/ocr-poc/pages/OcrPocPage'));
const OcrCaptureDebugPage = lazy(() => import('@/features/ocr-poc/pages/OcrCaptureDebugPage'));
// POC only - not a real feature.
const CheetahParserPocPage = lazy(() => import('@/features/cheetah-parser-poc/pages/CheetahParserPocPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const IntakePage = lazy(() => import('@/features/intake/pages/IntakePage'));
const TicketListPage = lazy(() => import('@/features/tickets/pages/TicketListPage'));
const TicketDetailPage = lazy(() => import('@/features/tickets/pages/TicketDetailPage'));
const CustomerListPage = lazy(() => import('@/features/customers/pages/CustomerListPage'));
const CustomerDetailPage = lazy(() => import('@/features/customers/pages/CustomerDetailPage'));
const PartsPage = lazy(() => import('@/features/parts/pages/PartsPage'));
const TechniciansPage = lazy(() => import('@/features/technicians/pages/TechniciansPage'));
const PackagePickupPage = lazy(() => import('@/features/packagePickup/pages/PackagePickupPage'));
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const UsersPage = lazy(() => import('@/features/users/pages/UsersPage'));
const SalesPage = lazy(() => import('@/features/sales/pages/SalesPage'));
const SaleDetailPage = lazy(() => import('@/features/sales/pages/SaleDetailPage'));
const NewSalePage = lazy(() => import('@/features/sales/pages/NewSalePage'));
const InvoicesPage = lazy(() => import('@/features/invoices/pages/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('@/features/invoices/pages/InvoiceDetailPage'));
const NewInvoicePage = lazy(() => import('@/features/invoices/pages/NewInvoicePage'));
const PaymentsPage = lazy(() => import('@/features/payments/pages/PaymentsPage'));
const PaymentDetailPage = lazy(() => import('@/features/payments/pages/PaymentDetailPage'));
const CommissionsPage = lazy(() => import('@/features/commissions/pages/CommissionsPage'));
const AIDiagnosisPage = lazy(() => import('@/features/ai/pages/AIDiagnosisPage'));
const AIQuickIntakePage = lazy(() => import('@/features/ai/pages/AIQuickIntakePage'));
const QueuePage = lazy(() => import('@/features/queue/pages/QueuePage'));
const CashPage = lazy(() => import('@/features/cash/pages/CashPage'));
const ShiftDetailPage = lazy(() => import('@/features/cash/pages/ShiftDetailPage'));
const ProfilePage = lazy(() => import('@/features/profile/pages/ProfilePage'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Spinner size="lg" />
  </div>
);

// Redirects away from a page the current role isn't allowed to see. A ref (not state) tracks
// whether this exact denial was already toasted, so React 18 StrictMode's double-invoke in dev
// doesn't show the toast twice for the same navigation.
const AccessDenied = ({ homePath = '/dashboard' }: { homePath?: string }) => {
  const toasted = useRef(false);

  useEffect(() => {
    if (toasted.current) return;
    toasted.current = true;
    toast.error('אין לך הרשאה לעמוד זה');
  }, []);

  return <Navigate to={homePath} replace />;
};

// Protected route wrapper - also enforces which pages the current role may reach (see
// @/utils/permissions). This blocks direct URL navigation to a hidden page, not just the
// sidebar link - the sidebar filtering alone would still let someone type /reports in manually.
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasNavAccess(user?.role, location.pathname)) {
    return <AccessDenied homePath={user?.role === 'KioskIntake' ? '/kiosk' : '/dashboard'} />;
  }

  return <>{children}</>;
};

// Silently re-authenticates on load using the httpOnly refresh-token cookie (see AuthController's
// SetRefreshTokenCookie). Fixes boss item ב (2026-08-06): before this, the access token lived only
// in memory (see authStore.ts's SECURITY NOTE), so every page reload/browser restart woke up with
// isAuthenticated=false and nothing left to try - the user had to log in again regardless of how
// much life was actually left on the refresh token (7 days server-side). Runs once at the app root,
// before ProtectedRoute would otherwise redirect to /login off that stale false.
function useSilentReauth() {
  useEffect(() => {
    if (useAuthStore.getState().isAuthenticated) {
      // Already resolved synchronously (e.g. the E2E dev branch in authStore's
      // onRehydrateStorage) - nothing to do, and this must not overwrite that with a real
      // network round-trip.
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const refreshRes = await api.post('/auth/refresh');
        const { accessToken } = refreshRes.data.data;
        if (!accessToken) {
          throw new Error('Refresh response did not include an access token');
        }
        if (cancelled) return;

        useAuthStore.getState().setTokens(accessToken);

        // A same-tab reload keeps `user` in sessionStorage (see authStore's partialize) - only a
        // fully new browser session (closed and reopened, cookie still valid up to 7 days) loses
        // it, so only fetch a fresh profile when there's nothing to fall back on.
        let user = useAuthStore.getState().user;
        if (!user) {
          const meRes = await api.get('/auth/me');
          if (cancelled) return;
          // GetProfile returns UserResponse fields flat (no tenantId - that lives in tenantStore,
          // already persisted to localStorage independently of this flow).
          user = { ...meRes.data.data, tenantId: useTenantStore.getState().tenantId ?? '' } as AuthUser;
        }

        if (cancelled) return;
        useAuthStore.setState({ user, isAuthenticated: true, isLoading: false });
      } catch {
        // No cookie, expired, or revoked - same outcome as never having been logged in.
        if (!cancelled) {
          useAuthStore.getState().logout();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);
}

function App() {
  useSilentReauth();

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/track/:token" element={<TrackingPage />} />
          <Route path="/self-intake/:token" element={<CustomerIntakePage />} />
          <Route path="/ocr-poc" element={<OcrPocPage />} />
          <Route path="/ocr-capture-debug" element={<OcrCaptureDebugPage />} />
          <Route path="/cheetah-parser-poc" element={<CheetahParserPocPage />} />

          {/* Kiosk: protected (needs the KioskIntake login) but full-screen, no sidebar/MainLayout */}
          <Route
            path="/kiosk"
            element={
              <ProtectedRoute>
                <KioskPage />
              </ProtectedRoute>
            }
          />

          {/* Protected routes with layout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="intake" element={<IntakePage />} />
            <Route path="intake/send-link" element={<SelfIntakeSendPage />} />
            <Route path="tickets" element={<TicketListPage />} />
            <Route path="tickets/:id" element={<TicketDetailPage />} />
            <Route path="customers" element={<CustomerListPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />
            <Route path="inventory" element={<PartsPage />} />
            <Route path="technicians" element={<TechniciansPage />} />
            <Route path="package-pickup" element={<PackagePickupPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="sales" element={<SalesPage />} />
            <Route path="sales/new" element={<NewSalePage />} />
            <Route path="sales/:id" element={<SaleDetailPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="invoices/new" element={<NewInvoicePage />} />
            <Route path="invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="payments/:id" element={<PaymentDetailPage />} />
            <Route path="commissions" element={<CommissionsPage />} />
            <Route path="queue" element={<QueuePage />} />
            <Route path="ai/diagnosis" element={<AIDiagnosisPage />} />
            <Route path="ai/quick-intake" element={<AIQuickIntakePage />} />
            <Route path="cash" element={<CashPage />} />
            <Route path="cash/shifts/:id" element={<ShiftDetailPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          {/* 404 fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
